import OfficeSelect from "@/components/OfficeSelect";
import { CirclePlusIcon } from "@/icons";
import { Button, Flex, Form, Input, Switch } from "antd";
import { useEffect } from "react";
import {
  useCreateLeavePolicyWithMultiOfficeMutation,
  useGetSingleLeavePolicyQuery,
  useUpdateLeavePolicyMutation,
} from "@/app/features/control-panel/policies/policies.api";
import CustomDrawer from "@/components/CustomDrawer";
import type { CustomModalProps } from "@/components/CustomModal";
import WithLoader from "@/components/WithLoader";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import LeaveTypes from "./LeaveTypes";
import { cn } from "@/lib/cn";

type Props = CustomModalProps & { id?: string; type: string | undefined };
const ManageLeavePolicy = ({ open, handleClose, id, type }: Props) => {
  const isEditMode = !!id;
  const [form] = Form.useForm();

  const {
    data: policyData,
    isLoading,
    isFetching,
  } = useGetSingleLeavePolicyQuery(id!, {
    skip: !id,
  });

  const [createLeavePolicyWithMultiOffice, { isLoading: isCreating }] =
    useCreateLeavePolicyWithMultiOfficeMutation();
  const [updateLeavePolicy, { isLoading: isUpdating }] =
    useUpdateLeavePolicyMutation();

  // Set form values when policy data is loaded
  useEffect(() => {
    if (policyData?.data && id) {
      const formData = {
        ...policyData.data,
        status: policyData.data.status === "active",
        leave_types: policyData.data.leave_types?.map((leaveType: any) => {
          return {
            name: leaveType.name,
            status: leaveType.status === "active",
            annual_limit: leaveType.annual_limit,
            max_at_a_time: leaveType.max_at_a_time,
            allow_previous_day: leaveType.allow_previous_day,
            line_manager_approval: leaveType.line_manager_approval,
            is_document_needed: leaveType.is_document_needed,
            max_limit_for_require_document:
              leaveType.max_limit_for_require_document,
            is_leave_encashment_enable: leaveType.is_leave_encashment_enable,
            minimum_tenure_in_days: leaveType.minimum_tenure_in_days,
            leave_encashment_max_limit: leaveType.leave_encashment_max_limit,
            leave_encashment_eligible_for:
              leaveType.leave_encashment_eligible_for,
            leave_encashment_rate_percentage_of:
              leaveType.leave_encashment_rate_percentage_of,
            leave_encashment_rate_percentage:
              leaveType.leave_encashment_rate_percentage
                ? Number(leaveType.leave_encashment_rate_percentage)
                : 0,
            is_carry_forward_enable: leaveType.is_carry_forward_enable,
            carry_forward_max_limit: leaveType.carry_forward_max_limit,
            policy_status: "completed",
          };
        }),
      };
      form.setFieldsValue(formData);
    } else if (!isEditMode) {
      form.setFieldsValue({
        status: true,
        leave_types: [
          {
            name: "Sick Leave",
            status: "active",
            annual_limit: 10,
            max_at_a_time: 10,
            allow_previous_day: true,
            line_manager_approval: true,
            is_document_needed: true,
            max_limit_for_require_document: 3,
            is_leave_encashment_enable: false,
            is_carry_forward_enable: false,
            policy_status: "completed",
          },
          {
            name: "Casual Leave",
            status: "active",
            annual_limit: 10,
            max_at_a_time: 1,
            allow_previous_day: false,
            line_manager_approval: true,
            is_document_needed: false,
            is_leave_encashment_enable: false,
            is_carry_forward_enable: false,
            policy_status: "completed",
          },
          {
            name: "Annual Leave",
            status: "active",
            annual_limit: 20,
            max_at_a_time: 10,
            allow_previous_day: false,
            line_manager_approval: true,
            is_document_needed: false,
            is_leave_encashment_enable: false,
            is_carry_forward_enable: false,
            policy_status: "completed",
          },
          {
            name: "Earned Leave",
            status: "active",
            annual_limit: 18,
            max_at_a_time: 10,
            allow_previous_day: false,
            line_manager_approval: true,
            is_document_needed: false,
            is_leave_encashment_enable: false,
            is_carry_forward_enable: false,
            policy_status: "completed",
          },
          {
            name: "Maternity Leave",
            status: "active",
            annual_limit: 112,
            max_at_a_time: 112,
            allow_previous_day: false,
            line_manager_approval: true,
            is_document_needed: false,
            is_leave_encashment_enable: false,
            is_carry_forward_enable: false,
            policy_status: "completed",
          },
          {
            name: "Paternity Leave",
            status: "active",
            annual_limit: 7,
            max_at_a_time: 7,
            allow_previous_day: false,
            line_manager_approval: true,
            is_document_needed: false,
            is_leave_encashment_enable: false,
            is_carry_forward_enable: false,
            policy_status: "completed",
          },
          {
            name: "Medical Leave",
            status: "active",
            annual_limit: 30,
            max_at_a_time: 30,
            allow_previous_day: true,
            line_manager_approval: true,
            is_document_needed: true,
            max_limit_for_require_document: 1,
            is_leave_encashment_enable: false,
            is_carry_forward_enable: false,
            policy_status: "completed",
          },
        ],
      });
    }
  }, [policyData, id, form, isEditMode, open]);

  const handleSubmit = async (values: any) => {
    const { leave_types, office_id, status, ...rest } = values || {};

    const formData: any = {
      ...rest,
      status: status ? "active" : "inactive",
      leave_types: leave_types?.map(
        ({ policy_status, status, ...rest }: any) => ({
          ...rest,
          status: status || status === "active" ? "active" : "inactive",
        }),
      ),
    };

    if (id) {
      formData.office_id = office_id;
    } else {
      formData.office_ids = office_id;
    }

    try {
      let response;
      if (id) {
        response = await updateLeavePolicy({ id, data: formData }).unwrap();
      } else {
        response = await createLeavePolicyWithMultiOffice(formData).unwrap();
      }

      // Check if API response has success: false
      if (response && response.success === false) {
        const message = getErrorMessage(
          response,
          `Failed to ${id ? "update" : "create"} leave policy`,
        );
        toast("error", message);
        return;
      }

      toast(
        "success",
        `Leave policy ${id ? "updated" : "created"} successfully`,
      );
      handleClose();
      form.resetFields();
    } catch (error: any) {
      const message = getErrorMessage(error);
      toast(
        "error",
        message || `Failed to ${id ? "update" : "create"} leave policy`,
      );
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={"Create Leave Policy"}
    >
      <WithLoader
        isLoading={isLoading || isFetching}
        className={cn(
          "rounded-lg",
          (isLoading || isFetching) && "h-[calc(100vh_-_105px)]",
        )}
      >
        <Form
          form={form}
          size="large"
          layout="vertical"
          disabled={type === "VIEW"}
          className="custom-form space-y-6"
          onFinish={handleSubmit}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            <Form.Item
              label="Policy Name"
              name="policy_name"
              rules={[{ required: true, message: "Please enter policy name" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="office_id"
              label="Select Office"
              rules={[{ required: true, message: "Please select office" }]}
            >
              <OfficeSelect
                mode={isEditMode ? undefined : "multiple"}
                placeholder="Select"
              />
            </Form.Item>

            <div className="md:col-span-2 mt-3">
              <Form.Item
                name="status"
                label="Status"
                layout="horizontal"
                className="border-b border-t !mb-0 !py-0.5 border-b-theme-gray-700 border-t-theme-gray-700 horizontal-end"
              >
                <Switch className="custom-switch" />
              </Form.Item>
            </div>

            <div className="md:col-span-2">
              <Form.List name="leave_types">
                {(fields, { add, remove }) => (
                  <>
                    <LeaveTypes
                      isEditMode={isEditMode}
                      form={form}
                      fields={fields}
                      onRemove={remove}
                    />

                    {type !== "VIEW" && (
                      <button
                        type="button"
                        onClick={() => {
                          add();
                        }}
                        className="w-full flex justify-center text-theme-primary relative"
                      >
                        {/* dashed line at bottom of icon */}
                        <div className="absolute left-0 right-0 w-full border-b border-theme-primary border-dashed bottom-2" />

                        <div className="bg-theme-slate-900 relative z-10 px-2">
                          <CirclePlusIcon size={20} />
                        </div>
                      </button>
                    )}
                  </>
                )}
              </Form.List>
            </div>
          </div>
          <div className="h-14" />
          {type !== "VIEW" && (
            <Flex
              justify="end"
              className="absolute left-0 right-0 bottom-0 py-4 px-6 bg-theme-slate-900 z-20 border-t border-theme-slate-800"
            >
              <Button
                htmlType="submit"
                className="btn-primary min-w-[80px] h-11"
                loading={id ? isUpdating : isCreating}
              >
                Save
              </Button>
            </Flex>
          )}
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
};

export default ManageLeavePolicy;
